import { lazy, memo, Suspense, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PhoneIphone as MobileIcon } from '@mui/icons-material';
import { Tooltip } from '@mui/material';

import { DeviceID } from '@playq/octopus2-devices';

import { unwrapEither } from '/api/service-hooks';
import { useLocalStorage, useModal } from '/hooks';
import { deviceToolkit, globalDispatch } from '/store/toolkits';
import { snackbarService } from '/common/snackbarService';
import { onlineDevicesEphemeral } from '/api/domains/devices';

import { BadgeStyled, ButtonStyled } from './styles';
import { LoadingTextProgress } from './ProgressText';
import type { StorageDevice, SubscribeDevice } from './types';
import { checkNameInDevice } from './helpers';

export const DialogDevice = lazy(
  /* webpackChunkName: "DialogDevice" */ () => import('./Dialog').then((module) => ({ default: module.DialogDevice }))
);

export const OnlineDevices = memo(() => {
  const subscriptionDevice = useSelector(deviceToolkit.selectors.subscriptionDevice);
  const subscriptions = useSelector(deviceToolkit.selectors.devices);

  const [loadingConnection, setLoadingConnection] = useState(false);

  const { open, close, isOpen } = useModal();
  const dispatch = useDispatch();

  const { subscribeDevice, unsubscribeDevice } = globalDispatch(dispatch);

  const [storedDevice, setStoredDevice, removeItem] = useLocalStorage<StorageDevice | null>(
    'DeviceID',
    null,
    handleDeviceSubscription
  );

  function handleDeviceSubscription(updatedData: StorageDevice | null) {
    if (updatedData) {
      subscribeDevice(checkNameInDevice(updatedData.device));
    } else {
      unsubscribeDevice(subscriptions[0]?.id);
    }
  }
  const subscribe = useCallback(
    ({ deviceID, device, reloadPage = false }: SubscribeDevice) => {
      unwrapEither(onlineDevicesEphemeral.sendSubscription(new DeviceID(deviceID)))
        .then((res) => {
          const status = { ...device.serialize(), connection: res.connection?.serialize() };
          setStoredDevice({ deviceID, device: status });
          if (reloadPage) {
            setLoadingConnection(false);
          }
        })
        .catch((err) => {
          removeItem();
          setLoadingConnection(false);
          console.error(err, 'Subscription error');
          snackbarService.error('Subscription error, check devtools');
        });
    },
    [removeItem, setStoredDevice]
  );
  useEffect(() => {
    let reconnectTimer: number | null = null;

    if (storedDevice !== null && !subscriptionDevice) {
      setLoadingConnection(true);

      reconnectTimer = window.setTimeout(() => {
        subscribe({
          deviceID: storedDevice.deviceID,
          device: checkNameInDevice(storedDevice.device),
          reloadPage: true,
        });
      }, 2000);
    }

    return () => {
      if (typeof reconnectTimer === 'number') {
        clearTimeout(reconnectTimer);
      }
    };
  }, [subscriptionDevice, subscribe, storedDevice]);

  return (
    <>
      <Tooltip title='View devices' placement='bottom'>
        <ButtonStyled onClick={open} color='inherit'>
          <BadgeStyled
            $offline={subscriptionDevice?.device.connection === undefined}
            invisible={subscriptionDevice?.device === undefined}
            variant='dot'
            anchorOrigin={{
              horizontal: 'left',
              vertical: 'bottom',
            }}
          >
            <MobileIcon />
          </BadgeStyled>
          {loadingConnection ? <LoadingTextProgress /> : subscriptionDevice?.name}
        </ButtonStyled>
      </Tooltip>
      {isOpen && (
        <Suspense>
          <DialogDevice
            close={close}
            handleLoadingConnection={setLoadingConnection}
            setStoredDevice={setStoredDevice}
            subscriptions={subscriptions}
            removeItem={removeItem}
            subscribe={subscribe}
          />
        </Suspense>
      )}
    </>
  );
});
